import { useFormikContext } from 'formik'

import { EMPTY_OBJECT } from '../../../../../constants/common'
import { SIZE } from '../../../../../constants/size'
import {
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../../../Form'
import { Content } from '../../../../Modal'
import {
  RentalPortalListingBuildingField,
  RentalPortalListingDepositAmountField,
  RentalPortalListingDepositTextField,
  RentalPortalListingDescriptionField,
  RentalPortalListingFlatField,
  RentalPortalListingRentPriceField,
  RentalPortalListingServiceChargeField,
  useRentalPortalListingPortalConnections,
} from '../../../../RentalPortalListing'
import {
  LISTING_FORM_FIELD_NAME_TIMING,
  LISTING_FORM_TIMING,
} from '../RentalPortalListingsForm.constants'
import { useRentalPortalListingsFormRestrictions } from '../RentalPortalListingsForm.hooks'
import {
  checkListingFormForEmptyRequired,
  getIsListingDraftTiming,
  getListingFormPublishNotifications,
} from '../RentalPortalListingsForm.utils'

import { AvailableFromField } from './AvailableFromField'
import { ListingsFormActions } from './ListingsFormActions'
import { PhotosField } from './PhotosField'
import { PortalsField } from './PortalsField'
import { ScheduleFields } from './ScheduleFields'

import * as styles from './RentalPortalListingsFormFields.module.css'

function RentalPortalListingsFormFields({
  ContentComponent = Content,
  contentComponentProps: {
    size: contentSize = SIZE.X_LARGE,
    ...contentComponentProps
  } = EMPTY_OBJECT,
  hasLoadError,
  hasPublishSection,
  isEditMode,
  isLoading,
  isReadOnly,
  listing,
  listingId,
  notifications,
  uploadingFiles,
  onCancel,
  onDeletePhoto,
  onRemoveNotification,
  onSetNotification,
  onUploadingFilesChange,
}) {
  const { dirty, isSubmitting, setFieldValue, submitForm, values } =
    useFormikContext()

  const { handleReloadPortals, hasPortalsError, isLoadingPortals, portals } =
    useRentalPortalListingPortalConnections()

  useFormValidationErrorsNotification({
    checkFormikFormEmptyRequired: checkListingFormForEmptyRequired,
    files: { filesUploading: uploadingFiles },
    onRemoveNotification,
    onSetNotification,
  })

  const {
    isPublishDisabled,
    isSubmitDisabled,
    isUnitLocked,
    notificationBlockReason,
    publishDisabledReason,
    submitTooltip,
  } = useRentalPortalListingsFormRestrictions({
    hasLoadError,
    hasPortalsError,
    hasPublishSection,
    isEditMode,
    isLoading,
    isLoadingPortals,
    isReadOnly,
    listing,
    portals,
  })

  function handleCancel() {
    onCancel({ dirty })
  }

  function handleClickSubmit() {
    setFieldValue(LISTING_FORM_FIELD_NAME_TIMING, LISTING_FORM_TIMING.scheduled)
  }

  function handleSaveDraft() {
    setFieldValue(
      LISTING_FORM_FIELD_NAME_TIMING,
      LISTING_FORM_TIMING.draft
    ).then(submitForm)
  }

  const isSavingDraft =
    isSubmitting &&
    getIsListingDraftTiming(values[LISTING_FORM_FIELD_NAME_TIMING])

  return (
    <FormBody
      Actions={ListingsFormActions}
      actionsProps={{
        ButtonAdd: false,
        ButtonClear: false,
        UploadComponent: false,
        cancelProps: {
          onClick: handleCancel,
        },
        className: styles.actions,
        hasSaveDraft: hasPublishSection,
        isSavingDraft,
        isSubmitting,
        saveDraftDisabled: isLoading || hasLoadError || isReadOnly,
        submitProps: {
          disabled: isSubmitDisabled,
          isLoading: isSubmitting && !isSavingDraft,
          title: isEditMode ? 'Modal.SaveBtnKey' : undefined,
          tooltip: submitTooltip,
          onClick: handleClickSubmit,
        },
        onSaveDraft: handleSaveDraft,
      }}
    >
      <ContentComponent {...contentComponentProps} size={contentSize}>
        <FormInlineNotifications
          notifications={getListingFormPublishNotifications({
            blockReason: notificationBlockReason,
            notifications,
          })}
        />
        <FormFieldsContainer className={styles.fieldsContainer}>
          {isUnitLocked ? null : (
            <RentalPortalListingBuildingField isLoading={isLoading} />
          )}
          <RentalPortalListingFlatField
            disabled={isUnitLocked || isReadOnly}
            isLoading={isLoading}
          />
          <RentalPortalListingRentPriceField isLoading={isLoading} />
          <RentalPortalListingServiceChargeField isLoading={isLoading} />
          <RentalPortalListingDepositAmountField isLoading={isLoading} />
          <RentalPortalListingDepositTextField isLoading={isLoading} />
          <AvailableFromField isLoading={isLoading} />
          <RentalPortalListingDescriptionField isLoading={isLoading} />
          <PhotosField
            isLoading={isLoading}
            listing={listing}
            listingId={listingId}
            onDeletePhoto={onDeletePhoto}
            onUploadingFilesChange={onUploadingFilesChange}
          />
          {hasPublishSection ? (
            <>
              <PortalsField
                disabled={isPublishDisabled}
                disabledReason={publishDisabledReason}
                hasPortalsError={hasPortalsError}
                isLoadingPortals={isLoadingPortals}
                portals={portals}
                onReloadPortals={handleReloadPortals}
                onRemoveNotification={onRemoveNotification}
                onSetNotification={onSetNotification}
              />
              <ScheduleFields disabled={isPublishDisabled} />
            </>
          ) : null}
        </FormFieldsContainer>
      </ContentComponent>
    </FormBody>
  )
}

export default RentalPortalListingsFormFields
